/**
 * The ONE list of built-in payment methods.
 *
 * Before 2026-09-25 the set `card | ach | wire | zelle | check` was written out
 * by hand in ~14 files with five separate label maps, and a method missing from
 * one of them did not fail to compile: it rendered as "Other" or vanished.
 * Everything that lists, labels or iterates the built-in methods reads this
 * registry; per-screen data (fee subtitles, icons, instruction renderers,
 * usability rules) is typed `Record<BuiltInMethodKey, …>` so a new entry here
 * fails to compile until every screen handles it.
 *
 * Labels are what customers and dealers see today — change one only on purpose.
 * `inSentence` is the form used mid-sentence ("Send your wire transfer…").
 *
 * Runtime-dependency-free on purpose: database.types.ts imports values from
 * here, so this file may only `import type` from it.
 */
import type {
  ManualPaymentMethod,
  MethodContexts,
  PaymentMethodsConfig,
  PaymentMethodsSnapshot,
} from './database.types'

export const PAYMENT_METHOD_REGISTRY = [
  { key: 'card', label: 'Credit card', inSentence: 'card', manual: false },
  { key: 'ach', label: 'Bank transfer (ACH)', inSentence: 'bank transfer', manual: false },
  { key: 'wire', label: 'Wire transfer', inSentence: 'wire transfer', manual: true },
  { key: 'zelle', label: 'Zelle', inSentence: 'Zelle', manual: true },
  { key: 'venmo', label: 'Venmo', inSentence: 'Venmo', manual: true },
  { key: 'check', label: 'Check by mail', inSentence: 'check', manual: true },
] as const

type RegistryEntry = (typeof PAYMENT_METHOD_REGISTRY)[number]
export type BuiltInMethodKey = RegistryEntry['key']
export type BuiltInManualMethod = Extract<RegistryEntry, { manual: true }>['key']

export const BUILT_IN_METHOD_KEYS: readonly BuiltInMethodKey[] = PAYMENT_METHOD_REGISTRY.map(m => m.key)
export const BUILT_IN_MANUAL_METHODS: readonly BuiltInManualMethod[] = PAYMENT_METHOD_REGISTRY
  .filter((m): m is Extract<RegistryEntry, { manual: true }> => m.manual)
  .map(m => m.key)

const BY_KEY = new Map<BuiltInMethodKey, RegistryEntry>(PAYMENT_METHOD_REGISTRY.map(m => [m.key, m]))

export function builtInMethodLabel(key: BuiltInMethodKey): string {
  return BY_KEY.get(key)?.label ?? key
}

export function builtInMethodInSentence(key: BuiltInMethodKey): string {
  return BY_KEY.get(key)?.inSentence ?? key
}

/** A manual method's name: the registry label, or a custom method's own label. */
export function manualMethodLabel(method: ManualPaymentMethod, otherLabel?: string | null): string {
  if (method === 'other') return otherLabel?.trim() || 'Other'
  return builtInMethodLabel(method)
}

/**
 * The bare Venmo handle. Dealers paste "@shop", "shop" or a profile URL; the
 * customer must see "@shop" exactly once.
 */
export function normalizeVenmoHandle(raw: string | undefined | null): string | undefined {
  if (!raw) return undefined
  let h = raw.trim()
  const url = h.match(/venmo\.com\/(?:u\/)?([^/?#\s]+)/i)
  if (url) {
    // Only a profile link names the account. A share, pay or sign-up link's
    // first path segment ("code", "pay") is a Venmo page, not a handle — saving
    // it would tell every customer to pay the wrong account.
    if (NOT_A_HANDLE.has(url[1].toLowerCase())) return undefined
    h = url[1]
  }
  h = h.replace(/^@+/, '').trim()
  return h || undefined
}

const NOT_A_HANDLE = new Set(['code', 'pay', 'account', 'signup', 'login', 'about', 'business', 'legal', 'u'])

const OFF: MethodContexts = { quotes: false, storefront: false }

function fillContexts(c: Partial<MethodContexts> | undefined): MethodContexts {
  return { quotes: c?.quotes ?? false, storefront: c?.storefront ?? false }
}

/**
 * A tenant's payment config with every built-in method present. Rows saved
 * before a method existed lack its key; every reader goes through this so an
 * absent method reads as off rather than throwing.
 */
export function normalizePaymentConfig(
  config: Partial<PaymentMethodsConfig> | null | undefined,
): PaymentMethodsConfig {
  const c = (config ?? {}) as Partial<Record<BuiltInMethodKey, { contexts?: Partial<MethodContexts> }>> & {
    other?: PaymentMethodsConfig['other']
  }
  const out = {} as Record<BuiltInMethodKey, unknown>
  for (const key of BUILT_IN_METHOD_KEYS) {
    const stored = c[key]
    const fallback = key === 'card' && !config ? { quotes: true, storefront: true } : OFF
    out[key] = { ...(stored ?? {}), contexts: stored ? fillContexts(stored.contexts) : fallback }
  }
  return {
    ...(out as unknown as Omit<PaymentMethodsConfig, 'other'>),
    other: (c.other ?? []).map(o => ({ ...o, contexts: fillContexts(o.contexts) })),
  }
}

/**
 * A stored order snapshot with every built-in method present. Historical
 * snapshots are left as they were sent to the customer; a method they predate
 * reads as `{ enabled: false }`.
 */
export function normalizePaymentSnapshot(
  snapshot: Partial<PaymentMethodsSnapshot> | null | undefined,
): PaymentMethodsSnapshot {
  const s = (snapshot ?? {}) as Partial<Record<BuiltInMethodKey, { enabled?: boolean }>> & {
    other?: PaymentMethodsSnapshot['other']
  }
  const out = {} as Record<BuiltInMethodKey, unknown>
  for (const key of BUILT_IN_METHOD_KEYS) {
    const stored = s[key]
    out[key] = stored ? { ...stored, enabled: stored.enabled === true } : { enabled: false }
  }
  return { ...(out as unknown as Omit<PaymentMethodsSnapshot, 'other'>), other: s.other ?? [] }
}

/** Whether a snapshot offers any pay-outside-Stripe method (built-in or custom). */
export function hasEnabledManualMethod(snapshot: PaymentMethodsSnapshot): boolean {
  return firstEnabledManualMethod(snapshot) !== null
}

/** The first enabled manual method in display order: a built-in key, or a custom method's id. */
export function firstEnabledManualMethod(
  snapshot: PaymentMethodsSnapshot,
): BuiltInManualMethod | { other: string } | null {
  for (const key of BUILT_IN_MANUAL_METHODS) {
    if (snapshot[key]?.enabled) return key
  }
  const o = (snapshot.other ?? []).find(m => m.enabled && m.label.trim() !== '')
  return o ? { other: o.id } : null
}
